import { ArrowLeft, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

const Privacy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary via-secondary to-accent text-white py-12">
        <div className="container mx-auto px-4">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="mb-4 text-white hover:bg-white/20"
          >
            <ArrowLeft className="mr-2 w-4 h-4" />
            Zurück zur Startseite / Back to Home
          </Button>
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8" />
            <h1 className="text-4xl font-serif font-bold">Datenschutzerklärung / Privacy Policy</h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto prose prose-lg">
          {/* German Version */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-4">Datenschutzerklärung (Deutsch)</h2>
            
            <h3 className="text-xl font-semibold mt-6 mb-3">1. Verantwortlicher</h3>
            <p>
              Verantwortlich für die Datenverarbeitung auf dieser Website ist:<br />
              <strong>Manfred Hahnel (Quetschnmanfred)</strong><br />
              München, Bayern, Deutschland<br />
              E-Mail: quetschnmanfred@gmail.com<br />
              Telefon: +49 174 9525 375
            </p>

            <h3 className="text-xl font-semibold mt-6 mb-3">2. Erhebung und Speicherung personenbezogener Daten</h3>
            <p>
              Bei der Nutzung unserer Website werden automatisch Informationen durch Ihren Browser an unseren Server übermittelt. 
              Diese Informationen werden temporär in einem sogenannten Logfile gespeichert. Folgende Informationen werden dabei 
              ohne Ihr Zutun erfasst und bis zur automatisierten Löschung gespeichert:
            </p>
            <ul>
              <li>IP-Adresse des anfragenden Rechners</li>
              <li>Datum und Uhrzeit des Zugriffs</li>
              <li>Name und URL der abgerufenen Datei</li>
              <li>Website, von der aus der Zugriff erfolgt (Referrer-URL)</li>
              <li>Verwendeter Browser und ggf. das Betriebssystem Ihres Rechners</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-3">3. Verwendung von Cookies</h3>
            <p>
              Diese Website verwendet nur technisch notwendige Cookies. Diese Cookies sind erforderlich, um die Funktionalität 
              der Website zu gewährleisten (z.B. Speicherung Ihrer Cookie-Einstellungen). Es werden keine Tracking- oder 
              Marketing-Cookies verwendet.
            </p>

            <h3 className="text-xl font-semibold mt-6 mb-3">4. Kontaktformular</h3>
            <p>
              Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem Anfrageformular 
              inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung der Anfrage und für den Fall von 
              Anschlussfragen bei uns gespeichert. Diese Daten geben wir nicht ohne Ihre Einwilligung weiter.
            </p>

            <h3 className="text-xl font-semibold mt-6 mb-3">5. Ihre Rechte</h3>
            <p>Sie haben folgende Rechte:</p>
            <ul>
              <li>Recht auf Auskunft über Ihre gespeicherten Daten</li>
              <li>Recht auf Berichtigung unrichtiger Daten</li>
              <li>Recht auf Löschung Ihrer Daten</li>
              <li>Recht auf Einschränkung der Verarbeitung</li>
              <li>Recht auf Datenübertragbarkeit</li>
              <li>Widerspruchsrecht gegen die Verarbeitung</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-3">6. Externe Links</h3>
            <p>
              Diese Website enthält Links zu externen Websites (z.B. WhatsApp, Social Media). Auf den Inhalt dieser 
              verlinkten Websites haben wir keinen Einfluss. Für die Inhalte der verlinkten Seiten ist stets der 
              jeweilige Anbieter oder Betreiber der Seiten verantwortlich.
            </p>

            <h3 className="text-xl font-semibold mt-6 mb-3">7. Änderungen der Datenschutzerklärung</h3>
            <p>
              Wir behalten uns vor, diese Datenschutzerklärung anzupassen, damit sie stets den aktuellen rechtlichen 
              Anforderungen entspricht oder um Änderungen unserer Leistungen in der Datenschutzerklärung umzusetzen.
            </p>

            <p className="mt-6">
              <strong>Stand: {new Date().toLocaleDateString('de-DE')}</strong>
            </p>
          </section>

          {/* English Version */}
          <section className="mt-12 pt-12 border-t-2">
            <h2 className="text-2xl font-bold mb-4">Privacy Policy (English)</h2>
            
            <h3 className="text-xl font-semibold mt-6 mb-3">1. Controller</h3>
            <p>
              The controller for data processing on this website is:<br />
              <strong>Manfred Hahnel (Quetschnmanfred)</strong><br />
              Munich, Bavaria, Germany<br />
              Email: quetschnmanfred@gmail.com<br />
              Phone: +49 174 9525 375
            </p>

            <h3 className="text-xl font-semibold mt-6 mb-3">2. Collection and Storage of Personal Data</h3>
            <p>
              When you use our website, information is automatically transmitted by your browser to our server. 
              This information is temporarily stored in a log file. The following information is collected 
              without your intervention and stored until automated deletion:
            </p>
            <ul>
              <li>IP address of the requesting computer</li>
              <li>Date and time of access</li>
              <li>Name and URL of the retrieved file</li>
              <li>Website from which access is made (referrer URL)</li>
              <li>Browser used and, if applicable, the operating system of your computer</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-3">3. Use of Cookies</h3>
            <p>
              This website only uses technically necessary cookies. These cookies are required to ensure the 
              functionality of the website (e.g., storing your cookie settings). No tracking or marketing 
              cookies are used.
            </p>

            <h3 className="text-xl font-semibold mt-6 mb-3">4. Contact Form</h3>
            <p>
              If you send us inquiries via the contact form, your information from the inquiry form, including 
              the contact details you provide, will be stored by us for the purpose of processing the inquiry 
              and in case of follow-up questions. We do not share this data without your consent.
            </p>

            <h3 className="text-xl font-semibold mt-6 mb-3">5. Your Rights</h3>
            <p>You have the following rights:</p>
            <ul>
              <li>Right to information about your stored data</li>
              <li>Right to correction of incorrect data</li>
              <li>Right to deletion of your data</li>
              <li>Right to restriction of processing</li>
              <li>Right to data portability</li>
              <li>Right to object to processing</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-3">6. External Links</h3>
            <p>
              This website contains links to external websites (e.g., WhatsApp, social media). We have no 
              influence on the content of these linked websites. The respective provider or operator of the 
              pages is always responsible for the content of the linked pages.
            </p>

            <h3 className="text-xl font-semibold mt-6 mb-3">7. Changes to the Privacy Policy</h3>
            <p>
              We reserve the right to adapt this privacy policy so that it always complies with current legal 
              requirements or to implement changes to our services in the privacy policy.
            </p>

            <p className="mt-6">
              <strong>Last updated: {new Date().toLocaleDateString('en-US')}</strong>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
